import type { KeyboardEvent } from 'react'
import { lifespans, type Lifespan } from '@/config/lifespans'

const INK = '#2C2926'

// Pentagon layout around a central "LifeSpan" hub. Circles overlap each other
// and the hub to convey how the five spans work together, each in its own
// color (Website Review V3).
const CENTER = 220
const RING = 112
const CIRCLE_R = 86
const HUB_R = 60
const ANGLES = [-90, -18, 54, 126, 198] // top, then clockwise

function pointAt(angleDeg: number, radius: number) {
  const rad = (angleDeg * Math.PI) / 180
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad),
  }
}

interface Props {
  activeId: Lifespan['id']
  onSelect: (id: Lifespan['id']) => void
}

export function LifespanVenn({ activeId, onSelect }: Props) {
  function handleKey(event: KeyboardEvent, id: Lifespan['id']) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(id)
    }
  }

  return (
    <div className="mx-auto w-full max-w-lg">
      <svg
        viewBox="0 0 440 440"
        role="group"
        aria-label="The five LifeSpans and how they overlap. Select a span to read more."
        className="h-auto w-full"
      >
        {/* Overlapping span circles, each in its own color */}
        {lifespans.map((span, i) => {
          const p = pointAt(ANGLES[i], RING)
          const isActive = span.id === activeId
          return (
            <g
              key={span.id}
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              aria-label={`${span.name}: ${span.tagline}`}
              onClick={() => onSelect(span.id)}
              onKeyDown={(e) => handleKey(e, span.id)}
              className="cursor-pointer outline-none [&:focus-visible>circle]:stroke-[3]"
            >
              <circle
                cx={p.x}
                cy={p.y}
                r={CIRCLE_R}
                fill={span.color}
                fillOpacity={isActive ? 0.55 : 0.28}
                stroke={span.color}
                strokeWidth={isActive ? 3 : 1.5}
                style={{ transition: 'fill-opacity 0.25s, stroke-width 0.25s' }}
              />
            </g>
          )
        })}

        {/* Central hub */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={HUB_R}
          fill="#FFFFFF"
          stroke={INK}
          strokeWidth={2}
        />
        <text
          x={CENTER}
          y={CENTER - 3}
          textAnchor="middle"
          fontSize="16"
          fontWeight="800"
          fill={INK}
          fontFamily="'Public Sans Variable', system-ui, sans-serif"
        >
          LifeSpan
        </text>
        <text
          x={CENTER}
          y={CENTER + 15}
          textAnchor="middle"
          fontSize="8.5"
          fontWeight="700"
          letterSpacing="1.5"
          fill={INK}
          opacity="0.55"
          fontFamily="'Public Sans Variable', system-ui, sans-serif"
        >
          THE WHOLE
        </text>

        {/* Labels — placed toward the outer edge of each circle, in its color */}
        {lifespans.map((span, i) => {
          const label = pointAt(ANGLES[i], RING + 36)
          const isActive = span.id === activeId
          const short = span.name.replace('Span', '')
          return (
            <text
              key={`label-${span.id}`}
              x={label.x}
              y={label.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={isActive ? '16' : '15'}
              fontWeight="800"
              fill={span.ink}
              fontFamily="'Public Sans Variable', system-ui, sans-serif"
              pointerEvents="none"
              style={{ transition: 'font-size 0.2s' }}
            >
              {short}
              <tspan fontSize="10" fontWeight="700">
                Span
              </tspan>
            </text>
          )
        })}
      </svg>

      <p className="mt-4 text-center text-xs font-bold uppercase tracking-[0.14em] text-ink-muted">
        Five spans, one connected LifeSpan. Select any to read more.
      </p>
    </div>
  )
}
